import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PageShell } from '@/components/Layout';
import { Device } from '@/lib/device';
import { useToast } from '@/lib/ui';
import { useAuth } from '@/hooks/useAuth';
import { useConfirmedMutation } from '@/api/mutations';
import { fetchUsers, updateUserFn, createUserFn, resetPasswordFn, fetchPostes } from '@/api/endpoints';
import type { User, UserUpdateInput, UserCreateInput } from '@/api/types';

type Role = User['role'];
const ROLES: Role[] = ['user', 'admin', 'superAdmin'];
const LOCALES: User['locale'][] = ['fr', 'en'];

const ROLE_BADGE: Record<Role, string> = {
  user: 'badge-slate',
  admin: 'badge-blue',
  superAdmin: 'badge-amber',
};

type PosteOpt = { id: number; meteor: string };

function fmtDate(s: string | null): string {
  return s ? s.replace('T', ' ').replace('Z', '') : '—';
}

export default function Users({ device }: { device: Device }) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<User | null>(null);
  const [creating, setCreating] = useState(false);
  const [createdPw, setCreatedPw] = useState<{ email: string; pw: string } | null>(null);
  const { user: me } = useAuth();
  const isSuperAdmin = me?.role === 'superAdmin';
  const toast = useToast();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({ queryKey: ['users'], queryFn: () => fetchUsers() });
  const users = data?.users ?? [];

  // postes for the default_poste combo (all data sources)
  const { data: postesData } = useQuery({
    queryKey: ['postes-min'],
    queryFn: () => fetchPostes({ limit: 500 }),
  });
  const posteOpts: PosteOpt[] = useMemo(
    () => (postesData?.postes ?? [])
      .map((p) => ({ id: p.id, meteor: p.meteor }))
      .sort((a, b) => a.meteor.localeCompare(b.meteor)),
    [postesData],
  );
  const meteorById = useMemo(() => {
    const m = new Map<number, string>();
    posteOpts.forEach((o) => m.set(o.id, o.meteor));
    return m;
  }, [posteOpts]);

  const updateMut = useConfirmedMutation(
    (vars: { id: string; patch: UserUpdateInput }) => updateUserFn(vars.id, vars.patch),
    {
      title: 'Modifier l’utilisateur',
      message: 'Confirmer la mise à jour de cet utilisateur ?',
      onSuccess: () => {
        toast.push('Utilisateur modifié', 'success');
        qc.invalidateQueries({ queryKey: ['users'] });
        setSelected(null);
      },
    },
  );

  const createMut = useConfirmedMutation(
    (input: UserCreateInput) => createUserFn(input),
    {
      title: 'Créer un utilisateur',
      message: 'Confirmer la création ? Un mot de passe temporaire sera généré.',
      onSuccess: (res) => {
        toast.push('Utilisateur créé', 'success');
        qc.invalidateQueries({ queryKey: ['users'] });
        setCreating(false);
        setCreatedPw({ email: res.user.email, pw: res.password_display });
      },
    },
  );

  const resetMut = useConfirmedMutation(
    (vars: { id: string; email: string }) => resetPasswordFn(vars.id),
    {
      title: 'Réinitialiser le mot de passe',
      message: 'Un nouveau mot de passe temporaire sera généré. Confirmer ?',
      destructive: true,
      onSuccess: (res, vars) => {
        toast.push('Mot de passe réinitialisé', 'success');
        setSelected(null);
        setCreatedPw({ email: vars.email, pw: res.password_display });
      },
    },
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q ? users.filter((u) => u.email.toLowerCase().includes(q) || u.role.toLowerCase().includes(q)) : users;
    return [...list].sort((a, b) => a.email.localeCompare(b.email));
  }, [users, search]);

  return (
    <PageShell device={device} title="Utilisateurs" breadcrumb="Administration">
      <div className="p-4 md:p-6 space-y-4">
        {createdPw && (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm flex items-start justify-between gap-3">
            <div>
              <div className="font-medium">Mot de passe temporaire pour {createdPw.email}</div>
              <div className="mt-1 font-mono text-base select-all">{createdPw.pw}</div>
              <div className="text-xs text-muted-foreground mt-1">Affiché une seule fois — copiez-le. L’utilisateur devra le changer à la 1ère connexion.</div>
            </div>
            <button onClick={() => setCreatedPw(null)} className="text-xs px-2 py-1 rounded border border-border hover:bg-muted">OK</button>
          </div>
        )}

        <div className="flex flex-wrap gap-2 items-center">
          <input
            type="search"
            placeholder="Rechercher email, rôle…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 px-3 rounded-md border border-input bg-background text-sm flex-1 min-w-[200px] max-w-md"
          />
          <span className="text-xs text-muted-foreground">
            {isLoading ? 'Chargement…' : `${filtered.length} utilisateurs`}
          </span>
          {isSuperAdmin && (
            <button
              onClick={() => setCreating(true)}
              className="ml-auto h-9 px-3 inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:opacity-90"
              title="Ajouter un utilisateur"
            >
              <span className="text-lg leading-none">+</span>
              <span className="hidden sm:inline">Nouvel utilisateur</span>
            </button>
          )}
        </div>

        <div className="bg-card border border-border rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-4 py-2.5 text-left">email</th>
                <th className="px-4 py-2.5 text-left">role</th>
                <th className="px-4 py-2.5 text-left">locale</th>
                <th className="px-4 py-2.5 text-left">default_poste</th>
                <th className="px-4 py-2.5 text-center">must_change_pw</th>
                <th className="px-4 py-2.5 text-left">last_login</th>
                <th className="px-4 py-2.5 text-center">actif</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((u) => (
                <tr key={u.id} className="hover:bg-accent/30 cursor-pointer" onClick={() => setSelected(u)}>
                  <td className="px-4 py-2.5 font-mono text-xs">{u.email}</td>
                  <td className="px-4 py-2.5">
                    <span className={`${ROLE_BADGE[u.role]} px-1.5 py-0.5 rounded text-[10px] font-medium`}>{u.role}</span>
                  </td>
                  <td className="px-4 py-2.5 uppercase text-xs">{u.locale}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">
                    {u.default_poste_id == null ? '—' : (meteorById.get(u.default_poste_id) ?? `#${u.default_poste_id}`)}
                  </td>
                  <td className="px-4 py-2.5 text-center">{u.must_change_password ? '⚠️' : '—'}</td>
                  <td className="px-4 py-2.5 font-mono text-[10px] text-muted-foreground">{fmtDate(u.last_login_at)}</td>
                  <td className="px-4 py-2.5 text-center">{u.disabled_at ? '🔴' : '🟢'}</td>
                </tr>
              ))}
              {!isLoading && filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-muted-foreground text-sm">Aucun utilisateur.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <UserDrawer
          mode="edit"
          user={selected}
          canEditRole={isSuperAdmin}
          canReset={isSuperAdmin || selected.id === me?.id}
          posteOpts={posteOpts}
          onClose={() => setSelected(null)}
          onSave={(patch) => updateMut.mutate({ id: selected.id, patch })}
          onReset={() => resetMut.mutate({ id: selected.id, email: selected.email })}
          isSaving={updateMut.isPending}
        />
      )}
      {creating && (
        <UserDrawer
          mode="create"
          canEditRole
          posteOpts={posteOpts}
          onClose={() => setCreating(false)}
          onSave={(patch) => createMut.mutate(patch as UserCreateInput)}
          isSaving={createMut.isPending}
        />
      )}
    </PageShell>
  );
}

function UserDrawer({
  mode,
  user,
  canEditRole,
  canReset,
  posteOpts,
  onClose,
  onSave,
  onReset,
  isSaving,
}: {
  mode: 'edit' | 'create';
  user?: User;
  canEditRole: boolean;
  canReset?: boolean;
  posteOpts: PosteOpt[];
  onClose: () => void;
  onSave: (patch: UserUpdateInput | UserCreateInput) => void;
  onReset?: () => void;
  isSaving: boolean;
}) {
  const isCreate = mode === 'create';
  const [email, setEmail] = useState(user?.email ?? '');
  const [role, setRole] = useState<Role>(user?.role ?? 'admin');
  const [locale, setLocale] = useState<User['locale']>(user?.locale ?? 'fr');
  const [defaultPoste, setDefaultPoste] = useState<string>(user?.default_poste_id?.toString() ?? '');
  const [mustChange, setMustChange] = useState(user?.must_change_password ?? true);

  const submit = () => {
    const n = Number(defaultPoste);
    const default_poste_id = defaultPoste.trim() === '' || !Number.isFinite(n) || n <= 0 ? null : n;
    if (isCreate) {
      onSave({ email, role, locale, default_poste_id, must_change_password: mustChange });
    } else {
      const patch: UserUpdateInput = { email, locale, must_change_password: mustChange, default_poste_id };
      if (canEditRole) patch.role = role;
      onSave(patch);
    }
  };

  return (
    <aside className="fixed inset-0 md:inset-auto md:top-0 md:right-0 md:bottom-0 md:w-[520px] bg-card border-l border-border shadow-2xl overflow-y-auto z-50 flex flex-col">
      <div className="sticky top-0 bg-background/95 backdrop-blur border-b border-border px-5 py-3 flex items-center justify-between">
        <div>
          <div className="text-xs text-muted-foreground">{isCreate ? 'Création utilisateur' : 'Édition utilisateur'}</div>
          <h2 className="text-lg font-bold font-mono">{isCreate ? 'Nouvel utilisateur' : user!.email}</h2>
        </div>
        <button onClick={onClose} aria-label="Fermer" className="p-2 rounded-md hover:bg-muted">✕</button>
      </div>

      <div className="p-5 space-y-4 flex-1">
        {!isCreate && <ReadField label="id" value={user!.id} mono />}

        <label className="block text-sm">
          <span className="font-medium">email</span>
          <input
            value={email}
            type="email"
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full h-9 px-3 rounded-md border border-input bg-background text-sm font-mono"
          />
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm">
            <span className="font-medium">role</span>
            {!canEditRole && <span className="ml-2 text-[10px] text-muted-foreground">superAdmin requis</span>}
            <select
              value={role}
              disabled={!canEditRole}
              onChange={(e) => setRole(e.target.value as Role)}
              className="mt-1 w-full h-9 px-2.5 rounded-md border border-input bg-background text-sm disabled:opacity-60 disabled:bg-muted"
            >
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
          <label className="block text-sm">
            <span className="font-medium">locale</span>
            <select
              value={locale}
              onChange={(e) => setLocale(e.target.value as User['locale'])}
              className="mt-1 w-full h-9 px-2.5 rounded-md border border-input bg-background text-sm"
            >
              {LOCALES.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </label>
        </div>

        <label className="block text-sm">
          <span className="font-medium">default_poste</span>
          <select
            value={defaultPoste}
            onChange={(e) => setDefaultPoste(e.target.value)}
            className="mt-1 w-full h-9 px-2.5 rounded-md border border-input bg-background text-sm"
          >
            <option value="">(aucun)</option>
            {posteOpts.map((o) => <option key={o.id} value={o.id}>{o.meteor}</option>)}
          </select>
        </label>

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={mustChange} onChange={(e) => setMustChange(e.target.checked)} className="h-4 w-4" />
          <span className="font-medium">must_change_password</span>
        </label>

        {isCreate ? (
          <p className="text-xs text-muted-foreground border-t border-border pt-3">
            Un mot de passe temporaire est généré côté serveur et affiché une seule fois après création.
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border">
            <ReadField label="has_totp" value={user!.has_totp ? 'oui' : 'non'} />
            <ReadField label="created_at" value={fmtDate(user!.created_at)} mono />
            <ReadField label="updated_at" value={fmtDate(user!.updated_at)} mono />
            <ReadField label="last_login_at" value={fmtDate(user!.last_login_at)} mono />
            <ReadField label="disabled_at" value={fmtDate(user!.disabled_at)} mono />
          </div>
        )}
      </div>

      <div className="sticky bottom-0 bg-background/95 backdrop-blur border-t border-border px-5 py-3 flex gap-2 justify-between">
        {!isCreate && canReset && onReset ? (
          <button
            onClick={onReset}
            className="h-9 px-4 rounded-md border border-amber-500 text-amber-600 text-sm hover:bg-amber-500/10"
          >
            Réinitialiser MDP
          </button>
        ) : <span />}
        <div className="flex gap-2">
          <button onClick={onClose} className="h-9 px-4 rounded-md border border-border text-sm">Annuler</button>
          <button
            onClick={submit}
            disabled={isSaving || !email.trim()}
            className="h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50"
          >
            {isSaving ? 'Sauvegarde…' : isCreate ? 'Créer' : 'Sauvegarder'}
          </button>
        </div>
      </div>
    </aside>
  );
}

function ReadField({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="text-sm">
      <span className="font-medium">{label}</span>
      <div className={`mt-1 w-full min-h-9 px-3 py-1.5 rounded-md border border-input bg-muted text-xs break-all ${mono ? 'font-mono' : ''}`}>
        {value}
      </div>
    </div>
  );
}
